import { useState, useEffect } from 'react';
import {
  Phone,
  Mail,
  Building,
  Clock,
  User,
  MessageSquare,
  ExternalLink,
  MapPin,
  CalendarPlus,
  Download,
  Play,
  Link2,
  HelpCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  formatTimestamp,
  getActionDeadline,
  getManualDeadline,
} from '@/lib/time';
import { useDebouncedCallback } from 'use-debounce';
import { DateTimeInlineEditor } from './DateTimeInlineEditor';
import {
  buildCalendarArtifacts,
  downloadICS,
  buildContactTitle,
  buildContactDescription,
  buildAppointmentDescriptionSafe,
  buildFollowupDescriptionSafe,
  getSuggestedStartTime,
} from '@/utils/calendarLinks';
import { CalendarDropdown } from '@/components/CalendarDropdown';
import {
  cleanActionPhrase,
  formatActionForDisplay,
  extractTimeContext,
  extractUrgencyLevel,
} from '@/utils/phraseCleanup';
import {
  resolveRelativeDate,
  resolveAppointmentDetails,
} from '@/lib/dateResolution';
import { summarizeOutboundActions } from '@/utils/outboundText';
import { loadActionTypeLabels } from '@/utils/actionTypesRegistry';

interface CallAction {
  id: string;
  call_id: string;
  campaign_id: string;
  prospect_id: string;
  type: string;
  confidence: number;
  phrase: string | null;
  action_data: any;
  processed: boolean;
  created_at: string;
  campaign?: {
    name: string;
    type?: string;
    businessContext?: {
      company: string;
      industry: string;
      context: string;
    };
  };
  mode?: string;
}

interface ContactInfo {
  name?: string;
  phone?: string;
  email?: string;
  company?: string;
}

interface OutboundContactSummaryProps {
  actions: CallAction[];
  processedActions?: CallAction[];
  contactInfo: ContactInfo;
  onMarkActionDone: (actionId: string) => void;
  onToggleActionProcessed?: (actionId: string) => void;
  onUpdateDueDate?: (actionId: string, dueAt: string) => Promise<void>;
  onOpenCallDetails?: (callId: string) => void;
}

export function OutboundContactSummary({
  actions,
  processedActions = [],
  contactInfo,
  onMarkActionDone,
  onToggleActionProcessed,
  onUpdateDueDate,
  onOpenCallDetails,
}: OutboundContactSummaryProps) {
  // Load action type labels for vault-driven summaries
  useEffect(() => {
    loadActionTypeLabels('outbound');
  }, []);

  // Debounce checkbox interactions to prevent rapid API calls
  const debouncedToggleProcessed = useDebouncedCallback(
    (actionId: string) => onToggleActionProcessed?.(actionId),
    300,
  );

  // Organize actions by type for summary - check both processed and unprocessed
  const allActions = [...actions, ...processedActions];
  const followupAction = allActions.find(
    (a) => a.type === 'follow_up_call' || a.type === 'consent_callback',
  );
  const emailAction = allActions.find(
    (a) => a.type === 'email_provided' || a.type === 'email_promise',
  );
  const appointmentAction = allActions.find(
    (a) => a.type === 'appointment_interest' || a.type === 'appointment_booked',
  );
  const appointmentBookedAction = allActions.find(
    (a) => a.type === 'appointment_booked',
  );
  const messageAction = allActions.find((a) => a.type === 'message_taking');

  // Extract appointment booking details with date resolution
  const appointmentDetails = appointmentBookedAction
    ? resolveAppointmentDetails(appointmentBookedAction)
    : null;

  // Get primary phone and email from contact info or action data
  const primaryPhone =
    contactInfo.phone ||
    actions.find((a) => a.action_data?.phone_number)?.action_data.phone_number;
  const primaryEmail = contactInfo.email || emailAction?.action_data?.email;
  const company =
    contactInfo.company ||
    actions.find((a) => a.action_data?.company)?.action_data.company;

  // Get next actions that need to be completed
  const pendingActions = actions.filter((a) => !a.processed);
  const nextAction = pendingActions.sort((a, b) => {
    // Use manual deadline if available, otherwise calculated deadline
    const deadlineA = a.action_data?.due_at
      ? getManualDeadline(a.action_data.due_at)
      : getActionDeadline(a.type, a.created_at, 'outbound');
    const deadlineB = b.action_data?.due_at
      ? getManualDeadline(b.action_data.due_at)
      : getActionDeadline(b.type, b.created_at, 'outbound');

    return deadlineA.isOverdue === deadlineB.isOverdue
      ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      : deadlineA.isOverdue
        ? -1
        : 1;
  })[0];

  const handleUpdateDueDate = async (newDueDate: Date) => {
    if (nextAction && onUpdateDueDate) {
      try {
        await onUpdateDueDate(nextAction.id, newDueDate.toISOString());
      } catch (error) {
        console.error(
          '[OutboundContactSummary] Due date update failed:',
          error,
        );
        throw error;
      }
    }
  };

  // Get call timestamp from the earliest action
  const callTimestamp =
    actions.length > 0
      ? actions.sort(
          (a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        )[0]?.created_at
      : null;

  // Use vault-driven summary (Paul's format)
  const actionSummaryData = summarizeOutboundActions(allActions);

  // Extract urgency from actions for badge display
  const getUrgencyText = () => {
    for (const action of allActions) {
      const urgency = extractUrgencyLevel(action.phrase || '');
      if (urgency) return urgency;
    }
    return null;
  };

  const actionSummary = actionSummaryData.summary;
  const urgencyText = getUrgencyText();

  return (
    <TooltipProvider>
      <Card className='p-6 space-y-4'>
        {/* Contact Header - Mirror inbound layout */}
        <div className='flex items-start justify-between'>
          <div className='space-y-2'>
            <div className='flex items-center gap-2'>
              <User className='h-4 w-4 text-muted-foreground' />
              <h3 className='font-semibold text-lg'>
                {contactInfo.name || 'Unknown Contact'}
              </h3>
              {urgencyText && (
                <Badge
                  variant={
                    urgencyText === 'ASAP'
                      ? 'destructive'
                      : urgencyText === 'Today'
                        ? 'secondary'
                        : 'outline'
                  }
                  className={`ml-2 ${
                    urgencyText === 'ASAP'
                      ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                      : urgencyText === 'Today'
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : urgencyText === 'Tomorrow'
                          ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                  }`}
                >
                  {urgencyText.toUpperCase()}
                </Badge>
              )}
            </div>

            {/* Call Timestamp */}
            {callTimestamp && (
              <div className='text-xs text-muted-foreground'>
                Call made: {formatTimestamp(callTimestamp)}
              </div>
            )}

            {/* Contact Information */}
            <div className='space-y-1'>
              {primaryPhone && (
                <div className='flex items-center gap-2 text-sm'>
                  <Phone className='h-3 w-3 text-muted-foreground' />
                  <span className='font-medium'>Primary:</span>
                  <span>{primaryPhone}</span>
                </div>
              )}
              {primaryEmail && (
                <div className='flex items-center gap-2 text-sm'>
                  <Mail className='h-3 w-3 text-muted-foreground' />
                  <span>{primaryEmail}</span>
                </div>
              )}
              {company && (
                <div className='flex items-center gap-2 text-sm'>
                  <Building className='h-3 w-3 text-muted-foreground' />
                  <span>{company}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Summary - Mirror inbound call summary styling, but show appointment booked banner if applicable */}
        <div className='border-t pt-4'>
          <h4 className='font-semibold text-base mb-3 flex items-center gap-2 text-foreground'>
            <MessageSquare className='h-4 w-4' />
            Call Summary
          </h4>

          {/* Show Appointment Booked Banner with calendar integration if appointment exists */}
          {appointmentBookedAction && appointmentDetails ? (
            <div className='space-y-4'>
              <div className='bg-primary/5 border border-primary/20 p-4 rounded-lg text-sm leading-relaxed font-medium'>
                {actionSummary}
              </div>

              {/* Calendar Integration for Appointments */}
              <div className='bg-green-50 border-2 border-green-200 p-4 rounded-lg'>
                <div className='flex items-center gap-2 mb-3'>
                  <CalendarPlus className='h-5 w-5 text-green-600' />
                  <span className='font-semibold text-green-800'>
                    Add to Calendar
                  </span>
                </div>
                <div className='mb-3'>
                  {appointmentDetails.startDate &&
                  appointmentDetails.endDate ? (
                    <CalendarDropdown
                      title={buildContactTitle(
                        contactInfo.name || 'Contact',
                        company,
                      )}
                      description={buildAppointmentDescriptionSafe(
                        primaryPhone,
                        company,
                        'Follow-up call appointment',
                        {
                          service: appointmentDetails.service,
                          location: appointmentDetails.location,
                          notes: appointmentDetails.notes,
                        },
                      )}
                      startDate={appointmentDetails.startDate}
                      endDate={appointmentDetails.endDate}
                      location={appointmentDetails.location || 'Phone call'}
                    />
                  ) : (
                    <div className='text-sm text-green-700'>
                      Calendar integration available once appointment time is
                      confirmed.
                    </div>
                  )}
                </div>
                {(appointmentDetails.service ||
                  appointmentDetails.location ||
                  appointmentDetails.notes) && (
                  <div className='space-y-1 text-sm'>
                    {appointmentDetails.service && (
                      <div className='text-green-700'>
                        <span className='font-medium'>Service:</span>{' '}
                        {appointmentDetails.service}
                      </div>
                    )}
                    {appointmentDetails.location && (
                      <div className='flex items-center gap-1 text-green-700'>
                        <MapPin className='h-3 w-3' />
                        {appointmentDetails.location}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className='bg-primary/5 border border-primary/20 p-4 rounded-lg text-sm leading-relaxed font-medium'>
              {actionSummary}
            </div>
          )}
        </div>

        {/* Actions Detail - Clean format */}
        <div className='border-t pt-4'>
          <h4 className='font-semibold text-sm mb-3'>Actions Detail</h4>
          <div className='space-y-3 pl-2'>
            {/* Follow-up Call */}
            <div className='flex items-start gap-3'>
              <Checkbox
                checked={followupAction?.processed || false}
                onCheckedChange={() =>
                  followupAction && debouncedToggleProcessed(followupAction.id)
                }
                className='mt-0.5'
              />
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  <span
                    className={
                      followupAction?.processed
                        ? 'line-through text-muted-foreground'
                        : followupAction
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                    }
                  >
                    Follow-up call
                  </span>
                  {followupAction &&
                    extractTimeContext(followupAction.phrase) && (
                      <>
                        <span className='text-muted-foreground'>→</span>
                        <span className='text-muted-foreground'>
                          {resolveRelativeDate(
                            extractTimeContext(followupAction.phrase),
                            followupAction.created_at
                              ? new Date(followupAction.created_at)
                              : new Date(),
                          )}
                        </span>
                      </>
                    )}
                </div>
              </div>
            </div>

            {/* Email */}
            <div className='flex items-start gap-3'>
              <Checkbox
                checked={emailAction?.processed || false}
                onCheckedChange={() =>
                  emailAction && debouncedToggleProcessed(emailAction.id)
                }
                className='mt-0.5'
              />
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  <span
                    className={
                      emailAction?.processed
                        ? 'line-through text-muted-foreground'
                        : emailAction
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                    }
                  >
                    Email info
                  </span>
                  {emailAction && primaryEmail && (
                    <>
                      <span className='text-muted-foreground'>→</span>
                      <span className='text-muted-foreground'>
                        {primaryEmail}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Appointment */}
            <div className='flex items-start gap-3'>
              <Checkbox
                checked={appointmentAction?.processed || false}
                onCheckedChange={() =>
                  appointmentAction &&
                  debouncedToggleProcessed(appointmentAction.id)
                }
                className='mt-0.5'
              />
              <div className='flex-1'>
                <div className='flex items-center gap-2'>
                  <span
                    className={
                      appointmentAction?.processed
                        ? 'line-through text-muted-foreground'
                        : appointmentAction
                          ? 'text-foreground'
                          : 'text-muted-foreground'
                    }
                  >
                    Appointment
                  </span>
                  {appointmentAction &&
                    appointmentDetails &&
                    (appointmentDetails.date || appointmentDetails.time) && (
                      <>
                        <span className='text-muted-foreground'>→</span>
                        <span className='text-muted-foreground'>
                          {appointmentDetails.date || appointmentDetails.time}
                        </span>
                      </>
                    )}
                </div>
              </div>
            </div>

            {/* Message taking */}
            {messageAction && (
              <div className='flex items-start gap-3'>
                <Checkbox
                  checked={messageAction.processed || false}
                  onCheckedChange={() =>
                    debouncedToggleProcessed(messageAction.id)
                  }
                  className='mt-0.5'
                />
                <div className='flex-1'>
                  <div className='flex items-center gap-2'>
                    <span className={messageAction?.processed ? 'line-through text-muted-foreground' : 'text-foreground'}>Message relay</span>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className='h-3 w-3 text-muted-foreground cursor-help' />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className='max-w-xs'>
                          Someone left a message that needs to be relayed to
                          another person or department. Check the call recording
                          for details.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Next Actions - show separate rows for email and follow-up when pending */}
        {(pendingActions.length > 0 || processedActions.length > 0) && (
          <div>
            <h4 className='font-semibold text-sm mb-3 flex items-center gap-2'>
              <Clock className='h-4 w-4' />
              {pendingActions.length > 0
                ? 'Next Actions'
                : 'Schedule Follow-up'}
            </h4>
            <div className='space-y-2 pl-6'>
              {/* Show pending email action */}
              {(() => {
                const pendingEmailAction = pendingActions.find(
                  (a) =>
                    a.type === 'email_provided' || a.type === 'email_promise',
                );
                if (!pendingEmailAction) return null;

                const deadline = pendingEmailAction.action_data?.due_at
                  ? getManualDeadline(pendingEmailAction.action_data.due_at)
                  : getActionDeadline(
                      pendingEmailAction.type,
                      pendingEmailAction.created_at,
                      'outbound',
                    );

                const actionText = primaryEmail
                  ? `Send info email to ${primaryEmail}`
                  : 'Send info email';

                return (
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>📧</span>
                      <span>{actionText}:</span>
                      <span
                        className={
                          deadline.isOverdue
                            ? 'text-destructive font-medium'
                            : 'text-foreground'
                        }
                      >
                        {deadline.text}
                      </span>
                      {pendingEmailAction.action_data?.due_at && (
                        <Badge
                          variant='outline'
                          className='text-xs px-1 py-0 ml-1'
                        >
                          Adjusted
                        </Badge>
                      )}
                    </div>
                    <div className='flex gap-2'>
                      {onUpdateDueDate && (
                        <DateTimeInlineEditor
                          currentDueDate={
                            pendingEmailAction.action_data?.due_at
                              ? new Date(pendingEmailAction.action_data.due_at)
                              : new Date(Date.now() + 36 * 60 * 60 * 1000)
                          }
                          onSave={async (newDueDate: Date) => {
                            try {
                              await onUpdateDueDate(
                                pendingEmailAction.id,
                                newDueDate.toISOString(),
                              );
                            } catch (error) {
                              console.error(
                                '[OutboundContactSummary] Email due date update failed:',
                                error,
                              );
                              throw error;
                            }
                          }}
                          isManuallyAdjusted={
                            !!pendingEmailAction.action_data?.due_at
                          }
                        />
                      )}
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => onMarkActionDone(pendingEmailAction.id)}
                        className='h-7'
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Show pending follow-up call action */}
              {(() => {
                const pendingFollowupAction = pendingActions.find(
                  (a) =>
                    a.type === 'follow_up_call' ||
                    a.type === 'consent_callback',
                );
                if (!pendingFollowupAction) return null;

                // If appointment is booked, use appointment date/time as the due date
                const deadline =
                  appointmentBookedAction && appointmentDetails?.startDate
                    ? {
                        text: appointmentDetails.date || 'Appointment time',
                        isOverdue: false,
                      }
                    : pendingFollowupAction.action_data?.due_at
                      ? getManualDeadline(
                          pendingFollowupAction.action_data.due_at,
                        )
                      : getActionDeadline(
                          pendingFollowupAction.type,
                          pendingFollowupAction.created_at,
                          'outbound',
                        );

                const handleUpdateFollowupDueDate = async (
                  newDueDate: Date,
                ) => {
                  if (onUpdateDueDate) {
                    try {
                      await onUpdateDueDate(
                        pendingFollowupAction.id,
                        newDueDate.toISOString(),
                      );
                    } catch (error) {
                      console.error(
                        '[OutboundContactSummary] Follow-up due date update failed:',
                        error,
                      );
                      throw error;
                    }
                  }
                };

                return (
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center gap-2 text-sm'>
                      <span className='text-muted-foreground'>📞</span>
                      <span>Follow-up call due:</span>
                      <span
                        className={
                          deadline.isOverdue
                            ? 'text-destructive font-medium'
                            : 'text-foreground'
                        }
                      >
                        {deadline.text}
                      </span>
                      {pendingFollowupAction.action_data?.due_at && (
                        <Badge
                          variant='outline'
                          className='text-xs px-1 py-0 ml-1'
                        >
                          Adjusted
                        </Badge>
                      )}
                      {onUpdateDueDate && (
                        <DateTimeInlineEditor
                          currentDueDate={
                            pendingFollowupAction.action_data?.due_at
                              ? new Date(
                                  pendingFollowupAction.action_data.due_at,
                                )
                              : new Date(Date.now() + 36 * 60 * 60 * 1000)
                          }
                          onSave={handleUpdateFollowupDueDate}
                          isManuallyAdjusted={
                            !!pendingFollowupAction.action_data?.due_at
                          }
                        />
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant='secondary' size='sm' className='h-7'>
                            <CalendarPlus className='h-3 w-3' />
                            Schedule
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className='z-50 w-56'>
                          <DropdownMenuItem
                            onClick={() => {
                              // Use appointment times if available, otherwise calculate suggested times
                              const suggestedStart =
                                appointmentBookedAction &&
                                appointmentDetails?.startDate
                                  ? appointmentDetails.startDate
                                  : getSuggestedStartTime(
                                      pendingFollowupAction,
                                      pendingFollowupAction.type,
                                      pendingFollowupAction.created_at,
                                      'outbound',
                                    );
                              const suggestedEnd =
                                appointmentBookedAction &&
                                appointmentDetails?.endDate
                                  ? appointmentDetails.endDate
                                  : new Date(
                                      suggestedStart.getTime() + 30 * 60 * 1000,
                                    );
                              const title = buildContactTitle(
                                contactInfo.name || 'Contact',
                                company,
                              );
                              const description = buildFollowupDescriptionSafe(
                                primaryPhone,
                                company,
                                'Follow-up call',
                              );

                              const { icsContent } = buildCalendarArtifacts({
                                title,
                                description,
                                start: suggestedStart,
                                end: suggestedEnd,
                                location: 'Phone call',
                              });

                              const filename = `follow-up-${(contactInfo.name || 'contact').replace(/\s+/g, '-').toLowerCase()}-${suggestedStart.getFullYear()}${String(suggestedStart.getMonth() + 1).padStart(2, '0')}${String(suggestedStart.getDate()).padStart(2, '0')}-${String(suggestedStart.getHours()).padStart(2, '0')}${String(suggestedStart.getMinutes()).padStart(2, '0')}.ics`;
                              downloadICS(filename, icsContent);
                            }}
                          >
                            <Download className='mr-2 h-4 w-4' />
                            Download .ics
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              // Use appointment times if available, otherwise calculate suggested times
                              const suggestedStart =
                                appointmentBookedAction &&
                                appointmentDetails?.startDate
                                  ? appointmentDetails.startDate
                                  : getSuggestedStartTime(
                                      pendingFollowupAction,
                                      pendingFollowupAction.type,
                                      pendingFollowupAction.created_at,
                                      'outbound',
                                    );
                              const suggestedEnd =
                                appointmentBookedAction &&
                                appointmentDetails?.endDate
                                  ? appointmentDetails.endDate
                                  : new Date(
                                      suggestedStart.getTime() + 30 * 60 * 1000,
                                    );
                              const title = buildContactTitle(
                                contactInfo.name || 'Contact',
                                company,
                              );
                              const description = buildFollowupDescriptionSafe(
                                primaryPhone,
                                company,
                                'Follow-up call',
                              );

                              const { googleUrl } = buildCalendarArtifacts({
                                title,
                                description,
                                start: suggestedStart,
                                end: suggestedEnd,
                                location: 'Phone call',
                              });

                              window.open(googleUrl, '_blank');
                            }}
                          >
                            <Link2 className='mr-2 h-4 w-4' />
                            Google Calendar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              // Use appointment times if available, otherwise calculate suggested times
                              const suggestedStart =
                                appointmentBookedAction &&
                                appointmentDetails?.startDate
                                  ? appointmentDetails.startDate
                                  : getSuggestedStartTime(
                                      pendingFollowupAction,
                                      pendingFollowupAction.type,
                                      pendingFollowupAction.created_at,
                                      'outbound',
                                    );
                              const suggestedEnd =
                                appointmentBookedAction &&
                                appointmentDetails?.endDate
                                  ? appointmentDetails.endDate
                                  : new Date(
                                      suggestedStart.getTime() + 30 * 60 * 1000,
                                    );
                              const title = buildContactTitle(
                                contactInfo.name || 'Contact',
                                company,
                              );
                              const description = buildFollowupDescriptionSafe(
                                primaryPhone,
                                company,
                                'Follow-up call',
                              );

                              const { outlookUrl } = buildCalendarArtifacts({
                                title,
                                description,
                                start: suggestedStart,
                                end: suggestedEnd,
                                location: 'Phone call',
                              });

                              window.open(outlookUrl, '_blank');
                            }}
                          >
                            <Link2 className='mr-2 h-4 w-4' />
                            Outlook Calendar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className='flex gap-2'>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() =>
                          onMarkActionDone(pendingFollowupAction.id)
                        }
                        className='h-7'
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Show calendar-only interface when no pending actions but processed actions exist */}
              {pendingActions.length === 0 && processedActions.length > 0 && (
                <div className='text-center py-4 text-muted-foreground'>
                  <div className='text-sm mb-3'>All actions completed.</div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant='secondary' size='sm'>
                        <CalendarPlus className='h-3 w-3 mr-1' />
                        Schedule Follow-up
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className='z-50 w-56'>
                      <DropdownMenuItem
                        onClick={() => {
                          // Use the most recent processed action for context
                          const recentAction =
                            processedActions[0] || allActions[0];
                          const suggestedStart = new Date(
                            Date.now() + 24 * 60 * 60 * 1000,
                          ); // Next business day
                          const suggestedEnd = new Date(
                            suggestedStart.getTime() + 30 * 60 * 1000,
                          );
                          const title = buildContactTitle(
                            contactInfo.name || 'Contact',
                            company,
                          );
                          const description = buildFollowupDescriptionSafe(
                            primaryPhone,
                            company,
                            'Follow-up call',
                          );

                          const { icsContent } = buildCalendarArtifacts({
                            title,
                            description,
                            start: suggestedStart,
                            end: suggestedEnd,
                            location: 'Phone call',
                          });

                          const filename = `follow-up-${(contactInfo.name || 'contact').replace(/\s+/g, '-').toLowerCase()}-${suggestedStart.getFullYear()}${String(suggestedStart.getMonth() + 1).padStart(2, '0')}${String(suggestedStart.getDate()).padStart(2, '0')}-${String(suggestedStart.getHours()).padStart(2, '0')}${String(suggestedStart.getMinutes()).padStart(2, '0')}.ics`;
                          downloadICS(filename, icsContent);
                        }}
                      >
                        <Download className='mr-2 h-4 w-4' />
                        Download .ics
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          const recentAction =
                            processedActions[0] || allActions[0];
                          const suggestedStart = new Date(
                            Date.now() + 24 * 60 * 60 * 1000,
                          );
                          const suggestedEnd = new Date(
                            suggestedStart.getTime() + 30 * 60 * 1000,
                          );
                          const title = buildContactTitle(
                            contactInfo.name || 'Contact',
                            company,
                          );
                          const description = buildFollowupDescriptionSafe(
                            primaryPhone,
                            company,
                            'Follow-up call',
                          );

                          const { googleUrl } = buildCalendarArtifacts({
                            title,
                            description,
                            start: suggestedStart,
                            end: suggestedEnd,
                            location: 'Phone call',
                          });

                          window.open(googleUrl, '_blank');
                        }}
                      >
                        <Link2 className='mr-2 h-4 w-4' />
                        Google Calendar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          const recentAction =
                            processedActions[0] || allActions[0];
                          const suggestedStart = new Date(
                            Date.now() + 24 * 60 * 60 * 1000,
                          );
                          const suggestedEnd = new Date(
                            suggestedStart.getTime() + 30 * 60 * 1000,
                          );
                          const title = buildContactTitle(
                            contactInfo.name || 'Contact',
                            company,
                          );
                          const description = buildFollowupDescriptionSafe(
                            primaryPhone,
                            company,
                            'Follow-up call',
                          );

                          const { outlookUrl } = buildCalendarArtifacts({
                            title,
                            description,
                            start: suggestedStart,
                            end: suggestedEnd,
                            location: 'Phone call',
                          });

                          window.open(outlookUrl, '_blank');
                        }}
                      >
                        <Link2 className='mr-2 h-4 w-4' />
                        Outlook Calendar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Restore processed actions if any exist */}
        {processedActions.length > 0 && (
          <div className='border-t pt-4 mt-4'>
            <h4 className='font-semibold text-sm mb-3'>Completed Actions</h4>
            <div className='space-y-2 text-sm'>
              {processedActions.map((action) => (
                <div
                  key={action.id}
                  className='flex items-center justify-between bg-muted/30 rounded-lg p-3'
                >
                  <div className='flex items-center gap-2'>
                    <span className='text-muted-foreground'>✓</span>
                    <span className='text-muted-foreground'>
                      {formatActionType(action.type)}
                    </span>
                    {action.phrase && (
                      <span className='text-xs text-muted-foreground italic'>
                        "{action.phrase.slice(0, 50)}
                        {action.phrase.length > 50 ? '...' : ''}"
                      </span>
                    )}
                  </div>
                  {onToggleActionProcessed && (
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => onToggleActionProcessed(action.id)}
                      className='h-7 text-xs'
                    >
                      Restore
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </TooltipProvider>
  );
}

function formatActionType(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}
